import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Button, Alert,
  Card, CardContent, Divider, IconButton, Tooltip,
  TableSortLabel, TablePagination,
} from '@mui/material';
import {
  Refresh, Close as CloseIcon, AccountBalance, Warning, Delete, Download,
} from '@mui/icons-material';
import axios from 'axios';
import { exportToExcel, fmtDateIST, fmtNum } from '../utils/exportExcel';

const IST_FMT = {
  timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric',
  hour: '2-digit', minute: '2-digit', hour12: true,
};
function toIST(d) {
  if (!d) return '-';
  let s = String(d);
  if (!s.endsWith('Z') && !s.includes('+')) s += 'Z';
  return new Date(s).toLocaleString('en-IN', IST_FMT);
}
function fmt(v) { return v != null ? Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'; }

const PAGE_OPTS = [10, 20, 50, 100, -1];

export default function LivePortfolio({ apiUrl }) {
  const [positions, setPositions] = useState([]);
  const [totalPositions, setTotalPositions] = useState(0);
  const [orders, setOrders] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [portfolio, setPortfolio] = useState(null);
  const [pnlSummary, setPnlSummary] = useState({ unrealized: 0, realized: 0 });
  const [brokerStatus, setBrokerStatus] = useState(null);
  const [brokerPositions, setBrokerPositions] = useState([]);
  const [brokerHoldings, setBrokerHoldings] = useState([]);
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(null);
  const [exporting, setExporting] = useState(false);

  const [posSort, setPosSort] = useState('desc');
  const [posSortBy, setPosSortBy] = useState('created_at');
  const [posPage, setPosPage] = useState(0);
  const [posRows, setPosRows] = useState(20);
  const [ordSort, setOrdSort] = useState('desc');
  const [ordSortBy, setOrdSortBy] = useState('created_at');
  const [ordPage, setOrdPage] = useState(0);
  const [ordRows, setOrdRows] = useState(20);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchMeta = useCallback(async () => {
    try {
      const [pnlRes, portRes, statusRes] = await Promise.all([
        axios.get(`${apiUrl}/orders/pnl-summary`, { headers, params: { trade_mode: 'real' } }),
        axios.get(`${apiUrl}/orders/portfolio`, { headers }),
        axios.get(`${apiUrl}/broker/status`, { headers }),
      ]);
      setPnlSummary(pnlRes.data);
      setPortfolio(portRes.data);
      setBrokerStatus(statusRes.data);

      if (statusRes.data?.connected) {
        const [bPosRes, bHoldRes, bLimRes] = await Promise.all([
          axios.get(`${apiUrl}/broker/positions/broker`, { headers }).catch(() => ({ data: { positions: [] } })),
          axios.get(`${apiUrl}/broker/holdings`, { headers }).catch(() => ({ data: { holdings: [] } })),
          axios.get(`${apiUrl}/broker/limits`, { headers }).catch(() => ({ data: { limits: null } })),
        ]);
        setBrokerPositions(bPosRes.data?.positions || []);
        setBrokerHoldings(bHoldRes.data?.holdings || []);
        setLimits(bLimRes.data?.limits || null);
      }
    } catch (err) { console.error(err); }
  }, [apiUrl]);

  const fetchPositions = useCallback(async () => {
    try {
      const params = {
        skip: posPage * posRows,
        limit: posRows === -1 ? 1000 : posRows,
        sort_by: posSortBy,
        sort_order: posSort,
        trade_mode: 'real',
        status: 'open',
      };
      const res = await axios.get(`${apiUrl}/orders/positions`, { headers, params });
      setPositions(res.data.items || []);
      setTotalPositions(res.data.total || 0);
    } catch (err) { console.error(err); }
  }, [apiUrl, posPage, posRows, posSortBy, posSort]);

  const fetchOrders = useCallback(async () => {
    try {
      const params = {
        skip: ordPage * ordRows,
        limit: ordRows === -1 ? 1000 : ordRows,
        sort_by: ordSortBy,
        sort_order: ordSort,
        trade_mode: 'real',
      };
      const res = await axios.get(`${apiUrl}/orders/`, { headers, params });
      setOrders(res.data.items || []);
      setTotalOrders(res.data.total || 0);
    } catch (err) { console.error(err); }
  }, [apiUrl, ordPage, ordRows, ordSortBy, ordSort]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchMeta(), fetchPositions(), fetchOrders()]);
    setLoading(false);
  }, [fetchMeta, fetchPositions, fetchOrders]);

  useEffect(() => { fetchMeta(); }, [fetchMeta]);
  useEffect(() => { fetchPositions(); }, [fetchPositions]);
  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const closePosition = async (id) => {
    setClosing(id);
    try {
      await axios.post(`${apiUrl}/orders/positions/${id}/close`, {}, { headers });
      fetchPositions();
      fetchMeta();
    } catch (err) { console.error(err); }
    finally { setClosing(null); }
  };

  const deleteOrder = async (orderId) => {
    try {
      await axios.delete(`${apiUrl}/orders/${orderId}`, { headers });
      fetchOrders();
      fetchMeta();
    } catch (err) { console.error(err); }
  };

  const computePnl = (p) => Math.round(((p.current_price || 0) - (p.entry_price || 0)) * (p.quantity || 0) * 100) / 100;
  const isConnected = brokerStatus?.connected;

  const handleExport = async () => {
    setExporting(true);
    try {
      const [posRes, ordRes] = await Promise.all([
        axios.get(`${apiUrl}/orders/positions`, { headers, params: { trade_mode: 'real', status: 'open', limit: 10000, sort_by: 'created_at', sort_order: 'desc' } }),
        axios.get(`${apiUrl}/orders/`, { headers, params: { trade_mode: 'real', limit: 10000, sort_by: 'created_at', sort_order: 'desc' } }),
      ]);
      const allPos = posRes.data.items || [];
      const allOrd = ordRes.data.items || [];

      const sheets = [
        {
          name: 'Summary',
          headers: ['Field', 'Value'],
          rows: [
            ['Export Date (IST)', fmtDateIST(new Date().toISOString())],
            ['Trade Mode', 'Live / Real'],
            ['Available Funds', limits ? fmtNum(parseFloat(limits.cash || 0)) : fmtNum((portfolio?.total_capital || 0) - (portfolio?.used_margin || 0))],
            ['Used Margin', fmtNum(portfolio?.used_margin)],
            ['Unrealized P&L', fmtNum(pnlSummary.unrealized)],
            ['Realized P&L', fmtNum(pnlSummary.realized)],
            ['Open Positions', allPos.length],
            ['Total Orders', allOrd.length],
            ['Broker', brokerStatus?.broker || 'N/A'],
            ['Broker Connected', isConnected ? 'Yes' : 'No'],
          ],
          colWidths: [28, 24],
        },
        {
          name: 'Open Positions',
          headers: ['Symbol', 'Exchange', 'Qty', 'Entry Price', 'Current Price', 'Unrealized P&L', 'Stop Loss', 'Take Profit', 'Source', 'Opened (IST)'],
          rows: allPos.map(p => [
            p.symbol, p.exchange || '', p.quantity,
            fmtNum(p.entry_price), fmtNum(p.current_price), fmtNum(p.unrealized_pnl),
            fmtNum(p.stop_loss), fmtNum(p.take_profit), p.source, fmtDateIST(p.created_at),
          ]),
        },
        {
          name: 'Orders',
          headers: ['Date Time (IST)', 'Strategy', 'Symbol', 'Exchange', 'Type', 'Qty', 'Price', 'Status', 'P&L', 'Source'],
          rows: allOrd.map(o => [
            fmtDateIST(o.created_at), o.strategy_name || '', o.symbol, o.exchange || '',
            o.order_type, o.quantity, fmtNum(o.entry_price), o.status, fmtNum(o.profit_loss), o.source,
          ]),
        },
      ];

      // Add broker data sheets if available
      if (brokerPositions.length > 0) {
        sheets.push({
          name: 'Broker Positions',
          headers: ['Symbol', 'Net Qty', 'Avg Price', 'LTP', 'Unrealised P&L', 'Product', 'Exchange'],
          rows: brokerPositions.map(p => [
            p.tsym || p.symbol || '', p.netqty || p.net_qty || '', p.netavgprc || p.avg_price || '',
            p.lp || p.ltp || '', p.rpnl || p.unrealized_pnl || '', p.prd || p.product || '', p.exch || p.exchange || '',
          ]),
        });
      }
      if (brokerHoldings.length > 0) {
        sheets.push({
          name: 'Holdings',
          headers: ['Symbol', 'Qty', 'Avg Price', 'LTP', 'P&L', 'Product', 'Exchange'],
          rows: brokerHoldings.map(h => [
            h.tsym || h.symbol || '', h.holdqty || h.qty || '', h.avgprc || h.avg_price || '',
            h.lp || h.ltp || '', h.npnl || h.pnl || '', h.prd || h.product || '', h.exch || h.exchange || '',
          ]),
        });
      }
      if (limits) {
        sheets.push({
          name: 'Account Limits',
          headers: ['Field', 'Value'],
          rows: Object.entries(limits).map(([k, v]) => [k, v]),
          colWidths: [30, 20],
        });
      }

      exportToExcel(`Live_Portfolio_${new Date().toISOString().slice(0, 10)}`, sheets);
    } catch (err) { console.error(err); }
    finally { setExporting(false); }
  };

  const handlePosSort = (col) => { setPosSort(posSortBy === col && posSort === 'asc' ? 'desc' : 'asc'); setPosSortBy(col); setPosPage(0); };
  const handleOrdSort = (col) => { setOrdSort(ordSortBy === col && ordSort === 'asc' ? 'desc' : 'asc'); setOrdSortBy(col); setOrdPage(0); };

  return (
    <Box data-testid="live-portfolio-page">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <AccountBalance sx={{ color: '#ff6f00' }} />
          <Typography variant="h5" fontWeight={700}>Live Trading Portfolio</Typography>
          <Chip label="REAL MONEY" size="small" sx={{ bgcolor: '#ff6f00', color: '#fff', fontWeight: 700 }} />
          {isConnected ? (
            <Chip label={`Broker: ${brokerStatus.broker}`} size="small" color="success" variant="outlined" />
          ) : (
            <Chip icon={<Warning />} label="Broker Offline" size="small" color="warning" variant="outlined" />
          )}
        </Box>
        <Box display="flex" gap={1}>
          <Button variant="outlined" size="small" startIcon={<Download />} onClick={handleExport}
            disabled={exporting} data-testid="export-live-portfolio-btn">
            {exporting ? 'Exporting...' : 'Export Excel'}
          </Button>
          <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={fetchAll} disabled={loading}
            data-testid="refresh-live-btn">Refresh</Button>
        </Box>
      </Box>

      {!isConnected && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Broker not connected. Go to <strong>Broker Config</strong> to login. Live positions from broker will appear once connected.
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Available Funds', value: limits ? fmt(parseFloat(limits.cash || 0)) : fmt((portfolio?.total_capital || 0) - (portfolio?.used_margin || 0)), color: '#e65100' },
          { label: 'Used Margin', value: limits ? fmt(parseFloat(limits.marginused || 0)) : fmt(portfolio?.used_margin || 0), color: '#bf360c' },
          { label: 'Open Positions', value: isConnected ? brokerPositions.length || totalPositions : totalPositions, color: '#ff6f00' },
          { label: 'Unrealized P&L', value: fmt(pnlSummary.unrealized), color: pnlSummary.unrealized >= 0 ? '#2e7d32' : '#c62828' },
          { label: 'Realized P&L', value: fmt(pnlSummary.realized), color: pnlSummary.realized >= 0 ? '#2e7d32' : '#c62828' },
        ].map((c, i) => (
          <Grid item xs={6} sm={2.4} key={i}>
            <Card sx={{ borderLeft: `4px solid ${c.color}` }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">{c.label}</Typography>
                <Typography variant="h6" fontWeight={700} sx={{ color: c.color }}>{c.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Broker Positions (live from broker) */}
      {isConnected && brokerPositions.length > 0 && (
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ px: 2, py: 1.5, bgcolor: 'rgba(255,111,0,0.08)' }}>
            <Typography variant="subtitle1" fontWeight={700}>Broker Positions (Live)</Typography>
          </Box>
          <Divider />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Symbol</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Net Qty</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Avg Price</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>LTP</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>P&L</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {brokerPositions.map((bp, i) => (
                  <TableRow key={i}>
                    <TableCell><Typography fontWeight="bold">{bp.tsym || bp.tradingsymbol || '-'}</Typography></TableCell>
                    <TableCell align="right">{bp.netqty || bp.quantity || 0}</TableCell>
                    <TableCell align="right">{fmt(parseFloat(bp.netavgprc || bp.avg_price || 0))}</TableCell>
                    <TableCell align="right">{fmt(parseFloat(bp.lp || bp.last_price || 0))}</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold"
                        color={parseFloat(bp.rpnl || bp.pnl || 0) >= 0 ? 'success.main' : 'error.main'}>
                        {fmt(parseFloat(bp.rpnl || bp.pnl || 0))}
                      </Typography>
                    </TableCell>
                    <TableCell>{bp.prd || bp.product || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* App-tracked Open Positions */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ px: 2, py: 1.5, bgcolor: 'rgba(255,111,0,0.08)' }}>
          <Typography variant="subtitle1" fontWeight={700}>Open Positions - App Tracked ({totalPositions})</Typography>
        </Box>
        <Divider />
        {positions.length === 0 ? (
          <Alert severity="info" sx={{ m: 1 }}>No open live positions tracked in app.</Alert>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      { id: 'symbol', label: 'Symbol' },
                      { id: 'quantity', label: 'Qty', align: 'right' },
                      { id: 'entry_price', label: 'Entry', align: 'right' },
                      { id: 'current_price', label: 'Current', align: 'right' },
                      { id: 'unrealized_pnl', label: 'Unrealized P&L', align: 'right' },
                    ].map(col => (
                      <TableCell key={col.id} align={col.align}>
                        <TableSortLabel active={posSortBy === col.id} direction={posSortBy === col.id ? posSort : 'asc'}
                          onClick={() => handlePosSort(col.id)}>{col.label}</TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell align="right">SL / TP</TableCell>
                    <TableCell>Opened</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {positions.map(p => {
                    const pnl = computePnl(p);
                    return (
                      <TableRow key={p.id}>
                        <TableCell>
                          <Typography fontWeight="bold">{p.symbol}</Typography>
                          <Typography variant="caption" color="text.secondary">{p.exchange}</Typography>
                        </TableCell>
                        <TableCell align="right">{p.quantity}</TableCell>
                        <TableCell align="right">{fmt(p.entry_price)}</TableCell>
                        <TableCell align="right">{fmt(p.current_price)}</TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold" color={pnl >= 0 ? 'success.main' : 'error.main'}>
                            {fmt(pnl)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption">{p.stop_loss ? fmt(p.stop_loss) : '-'} / {p.take_profit ? fmt(p.take_profit) : '-'}</Typography>
                        </TableCell>
                        <TableCell><Typography variant="caption">{toIST(p.created_at)}</Typography></TableCell>
                        <TableCell align="center">
                          <Tooltip title="Close Position">
                            <IconButton size="small" color="error" onClick={() => closePosition(p.id)} disabled={closing === p.id}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination rowsPerPageOptions={PAGE_OPTS.map(v => v === -1 ? { value: -1, label: 'All' } : v)}
              component="div" count={totalPositions} rowsPerPage={posRows} page={posPage}
              onPageChange={(e, p) => setPosPage(p)}
              onRowsPerPageChange={(e) => { setPosRows(parseInt(e.target.value)); setPosPage(0); }} />
          </>
        )}
      </Paper>

      {/* Holdings (from broker) */}
      {isConnected && brokerHoldings.length > 0 && (
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ px: 2, py: 1.5, bgcolor: 'rgba(255,111,0,0.08)' }}>
            <Typography variant="subtitle1" fontWeight={700}>Holdings</Typography>
          </Box>
          <Divider />
          <TableContainer sx={{ maxHeight: 250 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Symbol</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Qty</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Avg Price</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>P&L</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {brokerHoldings.map((h, i) => (
                  <TableRow key={i}>
                    <TableCell><Typography fontWeight="bold">{h.exch_tsym?.[0]?.tsym || h.tradingsymbol || '-'}</Typography></TableCell>
                    <TableCell align="right">{h.holdqty || h.quantity || 0}</TableCell>
                    <TableCell align="right">{fmt(parseFloat(h.upldprc || h.average_price || 0))}</TableCell>
                    <TableCell align="right">
                      <Typography color={parseFloat(h.rpnl || h.pnl || 0) >= 0 ? 'success.main' : 'error.main'}>
                        {fmt(parseFloat(h.rpnl || h.pnl || 0))}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Recent Live Orders */}
      <Paper>
        <Box sx={{ px: 2, py: 1.5, bgcolor: 'rgba(255,111,0,0.08)' }}>
          <Typography variant="subtitle1" fontWeight={700}>Recent Live Orders ({totalOrders})</Typography>
        </Box>
        <Divider />
        {orders.length === 0 ? (
          <Alert severity="info" sx={{ m: 1 }}>No live orders yet. Connect broker and place real trades from the Terminal.</Alert>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      { id: 'created_at', label: 'Time (IST)' },
                      { id: 'strategy_name', label: 'Strategy' },
                      { id: 'symbol', label: 'Symbol' },
                      { id: 'order_type', label: 'Type' },
                      { id: 'quantity', label: 'Qty', align: 'right' },
                      { id: 'entry_price', label: 'Price', align: 'right' },
                      { id: 'status', label: 'Status' },
                      { id: 'profit_loss', label: 'P&L', align: 'right' },
                    ].map(col => (
                      <TableCell key={col.id} align={col.align}>
                        <TableSortLabel active={ordSortBy === col.id} direction={ordSortBy === col.id ? ordSort : 'asc'}
                          onClick={() => handleOrdSort(col.id)}>{col.label}</TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell align="center"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.map(o => (
                    <TableRow key={o.id}>
                      <TableCell><Typography variant="caption">{toIST(o.created_at)}</Typography></TableCell>
                      <TableCell><Typography variant="caption" color="primary" fontWeight={600}>{o.strategy_name || '-'}</Typography></TableCell>
                      <TableCell><Typography fontWeight="bold">{o.symbol}</Typography></TableCell>
                      <TableCell>
                        <Chip label={o.order_type} size="small"
                          color={o.order_type === 'BUY' ? 'success' : o.order_type === 'SELL' ? 'error' : 'default'} />
                      </TableCell>
                      <TableCell align="right">{o.quantity}</TableCell>
                      <TableCell align="right">{fmt(o.entry_price)}</TableCell>
                      <TableCell><Chip label={o.status} size="small" /></TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={700} color={(o.profit_loss || 0) >= 0 ? 'success.main' : 'error.main'}>
                          {o.profit_loss != null ? fmt(o.profit_loss) : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Delete order">
                          <IconButton size="small" color="error" onClick={() => deleteOrder(o.id)}>
                            <Delete sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination rowsPerPageOptions={PAGE_OPTS.map(v => v === -1 ? { value: -1, label: 'All' } : v)}
              component="div" count={totalOrders} rowsPerPage={ordRows} page={ordPage}
              onPageChange={(e, p) => setOrdPage(p)}
              onRowsPerPageChange={(e) => { setOrdRows(parseInt(e.target.value)); setOrdPage(0); }} />
          </>
        )}
      </Paper>
    </Box>
  );
}
